import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SharedPagination from "../../modules/common/sharedPagination";
import "./style.css";
import { errorMessage } from "../../utlis/common";
import { DownloadInventoryList, FetchInventoryList } from "../../redux/features/inventory/inventoryAction";
import { CommonTable } from "../../modules/common/table/Table";
import { inventoryListColumns } from "../../modules/common/table/columns/_inventoryListColumns";
import InventoryModal from "./Inventory-modal";
import { setCloseInventoryModal, setOpenInventoryModal } from "../../redux/features/inventory/inventorySlice";
import * as XLSX from "xlsx";
import EditInventoryModal from "./EditInventoryModal";
import UrlFeedModal from "./UrlFeedModal";
import { FiSearch, FiLink } from "react-icons/fi";

const InventoryPage = () => {
  const dispatch: any = useDispatch();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUrlModal, setShowUrlModal] = useState(false);
  const limit = 10;

  const states = useSelector((state: any) => state.inventorySlice);
  const inventoryList = states?.inventory || [];
  const totalRecord = states?.totalRecord || 0;
  const totalPages = Math.ceil(totalRecord / limit);


  useEffect(() => {
    const fetchInventory = async () => {
      setLoading(true);
      try {
        await dispatch(FetchInventoryList({ page: currentPage, limit, search: searchQuery }));
      } catch (error) {
        console.error("Failed to fetch inventory:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [currentPage, searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFileUpload = (event) => {
    const fileInput = event.target;
    const file = fileInput.files[0];

    if (!file) {
      errorMessage("No file selected");
      return;
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      if (!target?.result) {
        errorMessage("FileReader failed to read the file");
        return;
      }

      let data: any[] = [];
      let headers: string[] = [];

      if (fileExtension === "csv") {
        const text = target.result as string;
        const rows = text.split(/\r?\n/).map(row => row.trim()).filter(row => row.length > 0);

        if (rows.length === 0) {
          errorMessage("CSV is empty!");
          fileInput.value = "";
          return;
        }

        //  Try to auto-detect the delimiter
        const delimiter = rows[0].includes(";") ? ";" : ",";

        headers = rows[0]?.split(delimiter).map(header => header.replace(/(^"|"$)/g, "").trim());
        if (!headers || headers.length === 0) {
          errorMessage("CSV Headers not found!");
          fileInput.value = "";
          return;
        }

        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(delimiter).map(value => value.replace(/(^"|"$)/g, "").trim());
          const record = headers.reduce((obj, header, index) => {
            obj[header] = isNaN(Number(values[index])) ? values[index] : Number(values[index]);
            return obj;
          }, {});

          data.push(record);
        }
      }
      else if (fileExtension === "xml") {
        const parser = new DOMParser();
        const xmlString = target.result as string;
        const xmlDoc = parser.parseFromString(xmlString, "application/xml");

        const items = xmlDoc.getElementsByTagName("item");
        if (items.length === 0) {
          errorMessage("Invalid XML format!");
          fileInput.value = "";
          return;
        }

        headers = Array.from(items).reduce((acc, item) => {
          const itemHeaders = Array.from(item.children).map(el => el.tagName);
          itemHeaders.forEach(header => {
            if (!acc.includes(header)) {
              acc.push(header);
            }
          });
          return acc;
        }, [] as string[]);

        if (headers.length === 0) {
          errorMessage("No headers found in XML items!");
          fileInput.value = "";
          return;
        }

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const record: { [key: string]: any } = {};

          headers.forEach(header => {
            record[header] = null;
          });

          const childElements = item.children;
          for (let j = 0; j < childElements.length; j++) {
            const element = childElements[j];
            const tagName = element.tagName;
            const value = element.textContent?.trim() || null;

            record[tagName] = value && !isNaN(Number(value)) ? Number(value) : value;
          }
          data.push(record);
        }
      }
      else if (fileExtension === "xlsx") {
        const binaryStr = target.result as string;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData: any = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!jsonData.length) {
          errorMessage("XLSX file is empty or invalid");
          fileInput.value = "";
          return;
        }

        headers = Object.keys(jsonData[0]);
        data = jsonData;
      }
      else {
        // errorMessage("Unsupported file type. Please upload a CSV or XML file.");
        errorMessage("Unsupported file type. Please upload a CSV, XLSX, or XML file.");
        fileInput.value = "";
        return;
      }

      console.log("Validated Data:", data);

      // If no data is valid, prevent upload
      if (data.length === 0) {
        errorMessage("No valid data found after validation.");
        fileInput.value = "";
        return;
      }

      // Validation rule: prezzo vendita <= 10000, costo articoli <= 10000, IVA <= 30
      const invalidRecords = data.filter((record) => {
        const prezzo = parseFloat(record["prezzo vendita"] || record["Prezzo Vendita"] || 0);
        const costo = parseFloat(record["costo articoli"] || record["Costo Articoli"] || 0);
        const iva = parseFloat(record["iva"] || record["IVA"] || 0);

        return prezzo > 10000 || costo > 10000 || iva > 30;
      });

      if (invalidRecords.length > 0) {
        errorMessage(
          `Some records exceed allowed limits: Prezzo/Costo > 10,000 or IVA > 30. Please correct and re-upload.`
        );
        fileInput.value = "";
        return;
      }

      try {
        await dispatch(setOpenInventoryModal({ data, headers }));
      } catch (error) {
        console.error("Failed to upload inventory CSV:", error);
      }

      //  Reset file input to allow re-uploading the same file
      fileInput.value = "";
    };

    if (fileExtension === "xlsx") {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsText(file);
    }
    // reader.readAsText(file);
  };

  const handleDownloadCSV = async () => {
    if (!inventoryList || inventoryList.length === 0) return;

    const response = await dispatch(DownloadInventoryList());
    const allInventory = response?.payload?.data || [];

    if (!allInventory || allInventory.length === 0) return;

    const headers = Object.keys(allInventory[0]).map(key => key.toString());
    const csvRows: any = [];

    csvRows.push(headers.join("," as string));

    allInventory.forEach(item => {
      const values = headers.map(header => {
        const val = item[header];
        const escaped = typeof val === "string" ? `"${val.replace(/"/g, '""')}"` : val;
        return escaped;
      });
      csvRows.push(values.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "inventory_export.csv";
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  return (
    <>
      <div className="order-status-container">
        <div className="inventory-header">
          <h2 className="page-title">Inventario</h2>

          <div className="inventory-actions">
            {/* Upload Button */}
            <div className="upload-container">
              <input
                type="file"
                accept=".csv, .xml, .xlsx"
                id="csvUpload"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
              <label htmlFor="csvUpload" className="upload-button">
                Carica file
              </label>
              <p className="upload-hint">* File accettati: CSV, XML o XLSX</p>
            </div>

            {/* URL Feed Button */}
            <div className="upload-container">
              <button
                className="upload-button url-feed-button"
                onClick={() => setShowUrlModal(true)}
              >
                <FiLink style={{ marginRight: 6 }} />
                Importa da URL
              </button>
              <p className="upload-hint">* JSON, CSV, XML dal feed esterno</p>
            </div>

            {/* Search Bar */}
            <div className="search-bar">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Cerca inventario..."
                className="form-control mb-3 search-text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

          </div>
        </div>

        <div className='orderListTable'>
          {loading ? (
            <p className="loading-text">Caricamento inventario...</p>
          ) : inventoryList?.length > 0 ? (
            <CommonTable data={inventoryList} columns={inventoryListColumns} />
          ) : (
            <p className="no-orders">Nessun inventario disponibile. Carica un catalogo CSV o XML per procedere.</p>
          )}
        </div>

        <div className="footer-actions">
          <button
            className="download-button"
            onClick={handleDownloadCSV}
            disabled={inventoryList.length === 0}
          >
            Scarica CSV
          </button>

          {totalRecord > 10 && <SharedPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />}
        </div>

        {states?.openInventoryModal && (
          <InventoryModal
            show={states?.openInventoryModal}
            handleClose={() => dispatch(setCloseInventoryModal())}
            data={states?.modalData?.data}
            headers={states?.modalData?.headers}
          />
        )}

        {states?.editInventoryModalOpen && (
          <EditInventoryModal />
        )}

        {showUrlModal && (
          <UrlFeedModal
            show={showUrlModal}
            handleClose={() => setShowUrlModal(false)}
          />
        )}

      </div>
    </>
  );
};

export default InventoryPage;
